import { ComponentType, createContext, useCallback, useState } from 'react';

export interface Props {
  children?: JSX.Element;
}

export interface StackedDialog<T> {
  component: ComponentType<T>;
  props?: T;
}

export type setDialogFn = <P>(component: ComponentType<P>, props?: P & Props) => void;

export const DialogContext = createContext<
  { unsetDialog: () => void; setDialog: setDialogFn; clearDialogStack: () => void } | undefined
>(undefined);

export const DialogProvider = (props: any) => {
  const [dialogStack, setDialogStack] = useState<StackedDialog<any>[]>([]);

  const unsetDialog = useCallback(() => {
    setDialogStack((current) => {
      current.pop();
      return [...current];
    });
  }, [setDialogStack]);

  const clearDialogStack = useCallback(() => {
    setDialogStack([]);
  }, [setDialogStack]);

  const setDialog = useCallback<setDialogFn>(
    (component, props?) => {
      setDialogStack((current) => [...current, { component, props }]);
    },
    [setDialogStack]
  );

  const renderDialogs = () =>
    dialogStack.map((stackedDialog, index) => {
      const {
        component: Component,
        props: { children, ...otherProps }
      } = stackedDialog;

      return (
        <div style={{ display: dialogStack.length - 1 === index ? 'block' : 'none' }} key={'dialog-' + index}>
          <Component {...otherProps}>{children}</Component>
        </div>
      );
    });

  return (
    <DialogContext.Provider
      value={{ unsetDialog: unsetDialog, setDialog: setDialog, clearDialogStack: clearDialogStack }}
      {...props}
    >
      {props.children}
      {renderDialogs()}
    </DialogContext.Provider>
  );
};
